import {
  Box,
  Grid,
  Typography,
  IconButton,
  Card,
  CardContent,
  Container,
  Fade,
  Zoom,
  Snackbar,
  Alert,
  styled,
  Backdrop,
  CircularProgress,
  Chip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  DeleteForever,
  Store,
  Email,
  Phone,
  LocationOn,
  CropFree,
  AttachMoney,
  Schedule,
  Business,
} from "@mui/icons-material";

import { deleteFranchise, getAllFranchises } from "../../actions/franchise";

const StyledContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  paddingTop: "2rem",
  paddingBottom: "2rem",
  width: "100%",
}));

const FranchiseCard = styled(Card)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(10px)",
  borderRadius: "16px",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  width: "100%",
  maxWidth: "600px",
  margin: "0 auto",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 30px rgba(0, 0, 0, 0.15)",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
  },
}));

const DeleteButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "16px",
  right: "16px",
  background: "rgba(244, 67, 54, 0.1)",
  color: "#f44336",
  zIndex: 10,
  "&:hover": {
    background: "rgba(244, 67, 54, 0.2)",
    transform: "scale(1.1)",
  },
  transition: "all 0.2s ease",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: "white",
  fontWeight: "700",
  marginBottom: "2rem",
  textAlign: "center",
  textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: "12px",
  gap: "12px",
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontWeight: "600",
  color: "#374151",
  fontSize: "0.9rem",
  minWidth: "140px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
}));

const InfoValue = styled(Typography)(({ theme }) => ({
  color: "#1f2937",
  fontSize: "0.9rem",
  flex: 1,
  wordBreak: "break-word",
}));

const StatsChip = styled(Chip)(({ theme }) => ({
  background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
  color: "white",
  fontWeight: "600",
  fontSize: "0.75rem",
  height: "24px",
  "& .MuiChip-label": {
    padding: "0 8px",
  },
}));

const Franchise = () => {
  const dispatch = useDispatch();
  const { franchises } = useSelector((state) => state.adminSlice);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const deleteOneFranchise = async (id) => {
    setLoading(true);
    try {
      dispatch(deleteFranchise({ id }));
      setToast({
        open: true,
        message: "Franchise deleted successfully!",
        severity: "success",
      });
    } catch (error) {
      setToast({
        open: true,
        message: "Failed to delete franchise",
        severity: "error",
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    dispatch(getAllFranchises());
  }, []);

  if (!franchises || franchises.length === 0) {
    return (
      <StyledContainer>
        <Container maxWidth="lg">
          <SectionTitle variant="h4">
            <Business sx={{ mr: 1, verticalAlign: "middle" }} />
            Franchise Management
          </SectionTitle>
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Store
              sx={{
                fontSize: "4rem",
                color: "rgba(255, 255, 255, 0.7)",
                mb: 2,
              }}
            />
            <Typography variant="h6" sx={{ color: "white", mb: 1 }}>
              No Franchises Found
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.8)" }}
            >
              There are currently no franchise applications to display.
            </Typography>
          </Box>
        </Container>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      <Container maxWidth="lg">
        <Backdrop
          open={loading}
          sx={{
            zIndex: 1010,
            backdropFilter: "blur(4px)",
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ color: "#667eea" }} />
        </Backdrop>

        <SectionTitle variant="h4">
          <Business sx={{ mr: 1, verticalAlign: "middle" }} />
          Franchise Management
        </SectionTitle>

        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <StatsChip
            label={`${franchises.length} Franchise${franchises.length !== 1 ? "s" : ""} Found`}
            icon={<Store sx={{ fontSize: "1rem !important" }} />}
          />
        </Box>

        <Grid container spacing={3}>
          {franchises.map((franchise, index) => (
            <Grid item xs={12} lg={6} key={franchise._id}>
              <Zoom in={true} style={{ transitionDelay: `${index * 100}ms` }}>
                <FranchiseCard>
                  <DeleteButton
                    onClick={() => deleteOneFranchise(franchise._id)}
                    size="small"
                  >
                    <DeleteForever />
                  </DeleteButton>

                  <CardContent sx={{ p: 3, pr: 6 }}>
                    {/* Header with Name */}
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "700",
                          color: "#1f2937",
                          fontSize: "1.2rem",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Store sx={{ color: "#667eea" }} />
                        {franchise.name}
                      </Typography>
                    </Box>

                    {/* Contact Information */}
                    <Box sx={{ mb: 2 }}>
                      <InfoRow>
                        <InfoLabel>
                          <Email sx={{ fontSize: "1rem", color: "#667eea" }} />
                          Email:
                        </InfoLabel>
                        <InfoValue>{franchise.email}</InfoValue>
                      </InfoRow>

                      <InfoRow>
                        <InfoLabel>
                          <Phone sx={{ fontSize: "1rem", color: "#667eea" }} />
                          Mobile:
                        </InfoLabel>
                        <InfoValue>{franchise.phone_no}</InfoValue>
                      </InfoRow>

                      <InfoRow>
                        <InfoLabel>
                          <LocationOn
                            sx={{ fontSize: "1rem", color: "#667eea" }}
                          />
                          Location:
                        </InfoLabel>
                        <InfoValue>{franchise.location}</InfoValue>
                      </InfoRow>
                    </Box>

                    {/* Business Details */}
                    <Box
                      sx={{
                        background: "rgba(103, 126, 234, 0.05)",
                        borderRadius: "8px",
                        padding: "12px",
                        border: "1px solid rgba(103, 126, 234, 0.1)",
                      }}
                    >
                      <InfoRow sx={{ mb: 1 }}>
                        <InfoLabel>
                          <CropFree
                            sx={{ fontSize: "1rem", color: "#667eea" }}
                          />
                          Available Area:
                        </InfoLabel>
                        <InfoValue sx={{ fontWeight: "600", color: "#667eea" }}>
                          {franchise.available_area}
                        </InfoValue>
                      </InfoRow>

                      <InfoRow sx={{ mb: 1 }}>
                        <InfoLabel>
                          <AttachMoney
                            sx={{ fontSize: "1rem", color: "#667eea" }}
                          />
                          Investment:
                        </InfoLabel>
                        <InfoValue sx={{ fontWeight: "600", color: "#10b981" }}>
                          {franchise.available_investment}
                        </InfoValue>
                      </InfoRow>

                      <InfoRow sx={{ mb: 0 }}>
                        <InfoLabel>
                          <Schedule
                            sx={{ fontSize: "1rem", color: "#667eea" }}
                          />
                          Timings:
                        </InfoLabel>
                        <InfoValue sx={{ fontWeight: "600", color: "#f59e0b" }}>
                          {franchise.suitable_timings}
                        </InfoValue>
                      </InfoRow>
                    </Box>
                  </CardContent>
                </FranchiseCard>
              </Zoom>
            </Grid>
          ))}
        </Grid>

        {/* Toast Notification */}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast({ ...toast, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setToast({ ...toast, open: false })}
            severity={toast.severity}
            variant="filled"
            sx={{
              borderRadius: "12px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
            }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </StyledContainer>
  );
};

export default Franchise;
